import { Controller } from '@hotwired/stimulus';
import { camera } from '@/lib/camera';
import { trackError } from '@/lib/errortracking';

export default class extends Controller {
  static targets = ['canvas'];
  canvasTarget!: HTMLCanvasElement;

  async connect(): Promise<void> {
    this.updateElementState('prompt'); // default while the permission query resolves
    const state = await camera.permissionState();
    this.updateElementState(state);
    if (state === 'granted') {
      this.requestVideo();
    }
  }

  disconnect(): void {
    camera.stopVideo();
  }

  updateElementState(permission: PermissionStatus['state'] | 'loading'): void {
    this.clearElementState();
    this.setElementState(permission);
  }

  clearElementState(): void {
    this.element.classList.remove('camera--initial-state');
    this.element.classList.remove('camera--ready-state');
    this.element.classList.remove('camera--denied-state');
    this.element.classList.remove('camera--loading-state');
  }

  setElementState(permission: PermissionStatus['state'] | 'loading'): void {
    switch (permission) {
      case 'prompt':
        this.element.classList.add('camera--initial-state');
        break;
      case 'granted':
        this.element.classList.add('camera--ready-state');
        break;
      case 'denied':
        this.element.classList.add('camera--denied-state');
        break;
      default:
        this.element.classList.add('camera--loading-state');
        break;
    }
  }

  async requestVideo(): Promise<void> {
    this.updateElementState('loading');
    try {
      await camera.requestVideo(this.canvasTarget);
      this.updateElementState('granted');
    } catch (error) {
      this.updateElementState('denied');
      const userDenied =
        error instanceof DOMException && error.name === 'NotAllowedError';
      if (!userDenied) {
        trackError(error instanceof Error ? error : new Error(String(error)), {
          context: 'camera#requestVideo',
        });
      }
    }
  }
}
